import React, { useState, useEffect } from 'react';
import { Card, Button, Container, Row, Col } from 'react-bootstrap';
import { FiFlag } from 'react-icons/fi'; // Import Report Icon
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const TrueFalseQuestion = ({ question, onNext }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  // Controls the Pro Subscription UI overlay in this component
  const [showProSubscription, setShowProSubscription] = useState(false);
  // New state to prevent re-showing after manual close
  const [manuallyClosed, setManuallyClosed] = useState(false);

  // Firestore and Auth instance
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();

  // If the user has already reached the incorrect answer limit,
  // and hasn't manually closed the overlay, show the Pro Subscription UI.
  useEffect(() => {
    const incorrectAnswerCount = parseInt(localStorage.getItem('incorrectAnswerCount')) || 0;
    if (incorrectAnswerCount >= 5 && !manuallyClosed) {
      setTimeout(() => {
        setShowProSubscription(true);
      }, 500);
    }
  }, [manuallyClosed]);

  // Maximum allowed incorrect answers
  const MAX_INCORRECT_ANSWERS = 5;

  // Handle option selection
  const handleOptionSelect = (isTrue) => {
    if (!showFeedback) {
      setSelectedOption(isTrue ? 1 : 2);
    }
  };

  // Handle answer submission
  const handleSubmit = async () => {
    if (selectedOption !== null) {
      const correct = selectedOption.toString() === question.correctanswer;
      setIsCorrect(correct);
      setShowFeedback(true);

      // Track incorrect answers using Local Storage
      let incorrectAnswerCount = parseInt(localStorage.getItem('incorrectAnswerCount')) || 0;
      const lastAttemptTimestamp = localStorage.getItem('lastAttemptTimestamp');
      const currentTimestamp = Date.now();

      // Reset count if 24 hours have passed
      if (lastAttemptTimestamp && currentTimestamp - lastAttemptTimestamp > 24 * 60 * 60 * 1000) {
        localStorage.setItem('incorrectAnswerCount', '0');
        incorrectAnswerCount = 0;
      }

      // Increase count only if answer is incorrect
      if (!correct) {
        incorrectAnswerCount += 1;
        localStorage.setItem('incorrectAnswerCount', incorrectAnswerCount);
        localStorage.setItem('lastAttemptTimestamp', currentTimestamp);
      }

      // If limit reached, show Pro Subscription overlay instead of navigating
      if (incorrectAnswerCount >= MAX_INCORRECT_ANSWERS) {
        setShowProSubscription(true);
        return;
      }

      // Log answer in Firebase
      try {
        const currentUser = auth.currentUser;
        await addDoc(collection(db, 'userpracticesessionanswers'), {
          date: serverTimestamp(),
          lessonid: question.lessonid,
          questionid: question.questionid,
          submittedanswerid: selectedOption === 1 ? 'True' : 'False',
          userid: currentUser ? currentUser.uid : 'anonymous',
          iscorrect: correct,
        });
        //console.log("Answer stored in DB with iscorrect:", correct);
      } catch (error) {
        console.error('Error logging answer:', error);
      }
    } else {
      alert('Please select an answer.');
    }
  };

  // Function to continue to the next question
  const handleContinue = () => {
    onNext(isCorrect); // Pass true (correct) or false (incorrect)
    setShowFeedback(false);
    setSelectedOption(null);
    setIsCorrect(false);
    setReportMessage('');
  };

  // Handle report submission (unchanged)
  const handleReport = async () => {
    try {
      const currentUser = auth.currentUser;
      await addDoc(collection(db, 'userreportedquestions'), {
        date: serverTimestamp(),
        questionid: question.questionid,
        userid: currentUser ? currentUser.uid : 'anonymous',
      });
      setReportMessage('Report submitted');
      setTimeout(() => setReportMessage(''), 4000);
    } catch (error) {
      console.error('Error reporting question:', error);
    }
  };

  return (
    <Container className="text-center my-5">
      <h3 className="mb-4">{question.questionname}</h3>

      <Row>
        <Col xs={6}>
          <Card
            className={`p-3 ${selectedOption === 1 ? (showFeedback ? (isCorrect ? 'border-success bg-success text-white' : 'border-danger bg-danger text-white') : 'border-info bg-info text-white') : ''}`}
            style={{ cursor: showFeedback ? 'default' : 'pointer' }}
            onClick={() => handleOptionSelect(true)}
          >
            <Card.Body>✅ True</Card.Body>
          </Card>
        </Col>
        <Col xs={6}>
          <Card
            className={`p-3 ${selectedOption === 2 ? (showFeedback ? (isCorrect ? 'border-success bg-success text-white' : 'border-danger bg-danger text-white') : 'border-info bg-info text-white') : ''}`}
            style={{ cursor: showFeedback ? 'default' : 'pointer' }}
            onClick={() => handleOptionSelect(false)}
          >
            <Card.Body>❌ False</Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-center align-items-center mt-4">
        {!showFeedback && (
          <Button onClick={handleSubmit} variant="warning">
            Submit
          </Button>
        )}
        {showFeedback && (
          <Button onClick={handleContinue} variant="warning">
            Continue
          </Button>
        )}
        <FiFlag
          size={28}
          style={{ cursor: 'pointer', color: '#FF7C24', marginLeft: '20px' }}
          onClick={handleReport}
          title="Report this question"
        />
      </div>

      {reportMessage && <p className="text-info mt-3">{reportMessage}</p>}

      {/* Pro Subscription Overlay */}
      {showProSubscription && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: '9999'
        }}>
          <Card className="p-4 mx-auto text-center"
            style={{ backgroundColor: '#E0F7FA', width: '90%', maxWidth: '500px', borderRadius: '15px', position: 'relative' }}>
            
            {/* ❌ Close Button */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowProSubscription(false);
                setManuallyClosed(true);
              }}
              style={{ position: 'absolute', top: '10px', right: '10px', border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#FF0000' }}>
              ❌
            </button>

            <h2 style={{ fontWeight: 'bold' }}>Upgrade to FinTorro Pro</h2>
            <p>Achieve your trading and investing goals powered by AI</p>

            <div>⭐⭐⭐⭐⭐</div>

            <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>“Helped me do a 180° transition!”</p>
            <p>Prior to FinTorro, I avoided the markets. - Jovin</p>
               {/* ✅ Features */}
        <div className="p-3" style={{ backgroundColor: '#B2EBF2', borderRadius: '10px' }}>
          <table className="table">
            <tbody>
              <tr><td>Daily Lessons</td> <td>✅</td></tr>
              <tr><td>Unlimited Courses</td> <td>✅</td></tr>
              <tr><td>Unlimited Practice</td> <td>✅</td></tr>
              <tr><td>AI Learning</td> <td>✅</td></tr>
              <tr><td>10,000 Lessons</td> <td>✅</td></tr>
              <tr><td>Join Challenges</td> <td>✅</td></tr>
              <tr><td>Includes Crypto, Forex, Psychology</td> <td>✅</td></tr>
            </tbody>
          </table>
        </div>
            <p>Cancel anytime | No Fees or Penalties</p>

            <h4>£59.99/yr (£4.99/mo)</h4>
            <Button style={{ backgroundColor: '#FF7C24', borderColor: '#FF7C24', width: '100%' }}>Join Now</Button>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default TrueFalseQuestion;
